import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getMemberPayments,
  getSubscriptionPaymentFailure,
  getUpdatePaymentLink,
  MemberPaymentItem,
  SubscriptionPaymentFailureResponse,
  syncMemberPayments
} from '../../../api/members';
import Button from '../../BaseComponents/Button';
import Modal from '../../BaseComponents/Modal';
import './MemberPaymentsModal.scss';

type MemberPaymentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  memberId?: string;
};

const formatPaymentDate = (dateValue?: string | null): string => {
  if (!dateValue) return '-';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const formatPaymentAmount = (
  payment: Pick<MemberPaymentItem, 'amount' | 'currency'>
): string => {
  const amountRaw = payment.amount;
  if (amountRaw === null || amountRaw === undefined || amountRaw === '') return '-';

  const amountAsNumber = Number(amountRaw);
  if (Number.isNaN(amountAsNumber)) {
    return String(amountRaw);
  }

  const currencyCode = payment.currency?.toUpperCase() || 'GBP';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2
  }).format(amountAsNumber);
};

const formatPaymentStatus = (payment: MemberPaymentItem): string => {
  const status = payment.payment_status || payment.status;
  if (!status) return '-';

  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

// Failed Charges report 'failed'; failed PaymentIntents without a Charge stay 'requires_payment_method'.
const FAILED_PAYMENT_STATUSES = ['failed', 'requires_payment_method'];

const isFailedPayment = (payment: MemberPaymentItem): boolean =>
  FAILED_PAYMENT_STATUSES.includes(payment.payment_status || payment.status || '');

const MemberPaymentsModal = ({ isOpen, onClose, memberId }: MemberPaymentsModalProps) => {
  const [payments, setPayments] = useState<MemberPaymentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentFailure, setPaymentFailure] =
    useState<SubscriptionPaymentFailureResponse | null>(null);
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  const loadPayments = useCallback(async () => {
    if (!memberId) return;

    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await getMemberPayments(memberId);
      setPayments(response?.payments || []);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.detail || error?.message || 'Failed to load payments'
      );
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [memberId]);

  // Subscription failure banner is best-effort: the payments list still renders if this call fails.
  const loadPaymentFailure = useCallback(async () => {
    if (!memberId) return;

    setPaymentFailure(null);
    try {
      const response = await getSubscriptionPaymentFailure(memberId);
      setPaymentFailure(response);
    } catch {
      setPaymentFailure(null);
    }
  }, [memberId]);

  useEffect(() => {
    if (!isOpen || !memberId) return;
    void loadPayments();
    void loadPaymentFailure();
  }, [isOpen, memberId, loadPayments, loadPaymentFailure]);

  const handleCopyUpdatePaymentLink = async () => {
    if (!memberId || isCopyingLink) return;

    try {
      setIsCopyingLink(true);
      const response = await getUpdatePaymentLink(memberId);
      await navigator.clipboard.writeText(response.url);
      toast.success('Update payment link copied to clipboard');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.description ||
          error?.message ||
          'Failed to copy update payment link'
      );
    } finally {
      setIsCopyingLink(false);
    }
  };

  const handleSync = async () => {
    if (!memberId || isSyncing || isLoading) return;

    try {
      setIsSyncing(true);
      const response = await syncMemberPayments(memberId);
      toast.success(
        `Synced ${response.synced_count} payment(s), skipped ${response.skipped_count}`
      );
      await Promise.all([loadPayments(), loadPaymentFailure()]);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.description ||
          error?.message ||
          'Failed to sync payments'
      );
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width='640px'
      closeOnOverlayClick
      closeOnEsc
      titleAction={
        <div className='payments-header'>
          <span className='payments-header-title'>Payments</span>
          <span
            className='payments-sync-wrap'
            data-tooltip='Sync payments'
          >
            <Button
              onClick={() => void handleSync()}
              isSecondary={true}
              variant='refresh'
              isLoading={isSyncing}
              isDisabled={isSyncing || !memberId || isLoading}
              height='32px'
              width='32px'
              paddingTop='6px'
              paddingBottom='6px'
              paddingLeft='6px'
              paddingRight='6px'
              borderRadius='10px'
              className='payments-sync-button'
            />
          </span>
        </div>
      }
    >
      <div className='member-payments-modal'>
        {!isLoading && !isSyncing && paymentFailure?.has_failed_payment && (
          <div className='payments-failure-banner'>
            <div className='payments-failure-details'>
              <span className='payments-failure-title'>Last subscription payment failed</span>
              <span className='payments-failure-reason'>
                {paymentFailure.failure_reason || 'Stripe did not provide a reason.'}
              </span>
              <span className='payments-failure-meta'>
                Amount due: {formatPaymentAmount({
                  amount: paymentFailure.amount_due,
                  currency: paymentFailure.currency
                })}
                {paymentFailure.next_payment_attempt
                  ? ` · Next retry: ${formatPaymentDate(paymentFailure.next_payment_attempt)}`
                  : ''}
              </span>
            </div>
            {paymentFailure.can_copy_update_payment_link && (
              <Button
                text='Copy update payment link'
                onClick={() => void handleCopyUpdatePaymentLink()}
                isSecondary={true}
                isLoading={isCopyingLink}
                isDisabled={isCopyingLink}
                height='36px'
                borderRadius='10px'
                className='payments-failure-copy-button'
              />
            )}
          </div>
        )}
        {isLoading || isSyncing ? (
          <div className='payments-state-message'>
            {isSyncing ? 'Syncing payments...' : 'Loading payments...'}
          </div>
        ) : errorMessage ? (
          <div className='payments-state-message payments-state-message--error'>
            {errorMessage}
          </div>
        ) : payments.length === 0 ? (
          <div className='payments-state-message'>No payments found for this member.</div>
        ) : (
          <table className='payments-table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={payment.id || `${payment.date}-${payment.amount}-${index}`}>
                  <td>{formatPaymentDate(payment.date)}</td>
                  <td>{formatPaymentAmount(payment)}</td>
                  <td>{payment.payment_method_type || '-'}</td>
                  <td>
                    {formatPaymentStatus(payment)}
                    {isFailedPayment(payment) && payment.failure_message && (
                      <div className='payments-failure-row-reason'>
                        {payment.failure_message}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
};

export default MemberPaymentsModal;
